import { Col, Row } from "react-bootstrap";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { useGet, usePost, usePut } from "@/apiHooks/HttpFunctions";

import BaseProductProductGroupInitialValues from "@/data/WebDataManagement/Mapping/BaseProductProductGroup/initial-values";
import BaseProductProductGroupSchema from "@/schema/WebDataManagement/Mapping/BaseProductProductGroup/BaseProductProductGroupSchema";
import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import FormFields from "./FormFields";
import { PRODUCT_GROUP_LABELS } from "@/constant/constants";
import RenderToast from "@/components/RenderToast";
import { productGroupsMappingsApi } from "@/apis/apis";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";

const BaseProductProductGroupModal = forwardRef(({ productGroup }, ref) => {
  const [modalState, setModalState] = useState({ isOpen: false });

  const showApi = modalState.id
    ? `${productGroupsMappingsApi}/${productGroup}/base-products/${modalState.id}`
    : null;

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const handleSubmit = (values) => {
    modalState.id ? saveEdit(values) : saveCreate(values);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: BaseProductProductGroupInitialValues(
      modalState.id ? showData : null
    ),
    validationSchema: BaseProductProductGroupSchema(productGroup),
    onSubmit: handleSubmit,
  });

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Base Product - Product Group: ${PRODUCT_GROUP_LABELS[productGroup]} ${action} successfully.`,
    });
  };
  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePut({
    apiURL: `${productGroupsMappingsApi}/${productGroup}/base-products/${modalState.id}`,
    onSuccess: handleSuccess("edited"),
  });
  const {
    mutate: saveCreate,
    isPending: saveCreateIsPending,
    error: saveCreateError,
  } = usePost({
    apiURL: `${productGroupsMappingsApi}/${productGroup}/base-products`,
    onSuccess: handleSuccess("added"),
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending, saveCreateIsPending],
    [saveEditError, saveCreateError]
  );

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [formik, resetError]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={`${
        modalState.id ? "Edit" : "Add"
      } Base Product - Product Group: ${PRODUCT_GROUP_LABELS[productGroup]}`}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"base_product_code"}
            saveError={saveError}
            label="Base Product ID"
          />
        </Col>
      </Row>
      <FormFields
        productGroup={productGroup}
        formik={formik}
        modalState={modalState}
        showData={showData}
        saveError={saveError}
      />
    </BasicModal>
  );
});

export default BaseProductProductGroupModal;
